import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, COMPANY } from "@/data/site";

const EASE = [0.16, 1, 0.3, 1];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-white/90 backdrop-blur-md border-b border-slate-200" : "bg-transparent"
      }`}
    >
      <div className="container-x flex h-24 items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img
            src={solid ? COMPANY.logo : COMPANY.logoOnDark}
            alt="Andrews Building Company logo"
            className="h-12 w-auto md:h-14"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link font-display text-sm uppercase tracking-widest transition-opacity hover:opacity-100 ${
                  solid ? "text-[#0A1128]" : "text-white"
                } ${isActive ? "active opacity-100" : "opacity-80"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-btn"
            className={`group hidden items-center gap-2 px-5 py-3 font-display text-sm uppercase tracking-widest transition-colors md:flex ${
              solid
                ? "bg-[#0A1128] text-white hover:bg-[#1D4ED8]"
                : "border border-white/60 text-white hover:bg-white hover:text-[#0A1128]"
            }`}
          >
            <Phone size={16} />
            {COMPANY.phone}
          </a>
          <button
            className={`lg:hidden ${solid ? "text-[#0A1128]" : "text-white"}`}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
          >
            <nav className="container-x flex flex-col py-6" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, ease: EASE }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block border-b border-slate-100 py-4 font-display text-xl uppercase tracking-wide text-[#0A1128]"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={COMPANY.phoneHref}
                data-testid="mobile-phone-btn"
                className="mt-6 flex items-center justify-center gap-2 bg-[#0A1128] px-5 py-4 font-display text-sm uppercase tracking-widest text-white"
              >
                <Phone size={16} /> {COMPANY.phone}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
